import { useLocalization } from "@fluent/react";
import { MenuButton } from "@narmi/design_system";
import { useDeleteSessionQuery } from "@lending/hooks/queries/useDeleteSessionQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useGetSessionQuery } from "@lending/hooks/queries/useGetSessionQuery";
import styles from "./UserMenu.module.css";
import { useLendingHost } from "../../contexts/LendingHostContext";

interface UserMenuTriggerProps {
  /** Applicant's full name, or "" when unknown. */
  name: string;
  isOpen: boolean;
}

/** Pill with the applicant's name (person icon when unknown, so it's never blank). */
function UserMenuTrigger({ name, isOpen }: UserMenuTriggerProps) {
  return (
    <span className={styles.trigger}>
      {name ? <span>{name}</span> : <span className="narmi-icon-user" aria-hidden="true" />}
      <span className={`narmi-icon-chevron-${isOpen ? "up" : "down"}`} aria-hidden="true" />
    </span>
  );
}

interface UserMenuProps {
  /**
   * Applicant name shown in the trigger. Callers supply it from whichever
   * source owns the name on their surface (the flow header's applicant fetch,
   * the portal's applications list); without one the trigger is icon-only.
   */
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Account menu shared by the lending flow header and the portal top nav.
 * Logout branches on `authMode`: `digital_banking` defers to the host's
 * `onLogout`; `lending_session` tears down its own sessions and redirects out.
 *
 * Rendered only for an authenticated visitor. The host has already
 * authenticated a `digital_banking` user; standalone auth lives in an httponly
 * cookie, so the menu waits on the session probe and renders nothing while the
 * probe is in flight or when it reports no session (there is nothing to log
 * out of on the pre-auth screens).
 */
export function UserMenu({ firstName, lastName }: UserMenuProps) {
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const { authMode, onLogout } = useLendingHost();

  const isStandalone = authMode === "lending_session";
  const { data: session } = useGetSessionQuery({ enabled: isStandalone });

  const { mutate: deleteStandaloneSession } = useDeleteSessionQuery({
    onSuccess: () => {
      // Fall back to the app root (→ product selection) when the FI website
      // isn't set; an empty URL would just reload the page instead of logging out.
      const lendingWebsite = institution.lending_website_flow_logo_nav_url ?? "";
      window.location.href = lendingWebsite.length > 0 ? lendingWebsite : "/";
    },
  });

  const handleLogout = () => {
    if (authMode === "digital_banking") {
      // Host owns the Digital Banking session, so it owns logout + redirect.
      onLogout?.();
    } else {
      deleteStandaloneSession();
    }
  };

  if (isStandalone && session?.authenticated !== true) {
    return null;
  }

  const name = [firstName, lastName].filter(Boolean).join(" ");

  return (
    <MenuButton
      label={name || l10n.getString("ui-account-menu", null, "Account menu")}
      renderTrigger={(isOpen: boolean) => <UserMenuTrigger name={name} isOpen={isOpen} />}
    >
      <MenuButton.Item
        label={l10n.getString("ui-logout", null, "Logout")}
        startIcon="log-out"
        onSelect={handleLogout}
      />
    </MenuButton>
  );
}
